import { CompanyDto } from "Api/Api";
import { CompanyEditForm } from "Components/Companies/CompanyEdit/CompanyEditForm";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useHistory } from "react-router-dom";
import {
  getCompaniesDetailAsync,
  saveCompanyAsync,
  resetCompanyDetail,
} from "State/Companies/CompanyReducer";
import { RootStateType } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import styled from "styled-components";
import { setDisableRedirect } from "State/App/AppReducer";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { CompaniesSideMenu } from "Components/Companies/CompaniesSideMenu";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import { isNoU } from "Utils/ObjectUtils";

type RouteProps = {
  id: string;
};

type Props = {
  isCreate: boolean;
  companiesListUrl: string;
};

const newCompany = {} as CompanyDto;

const StyledLayout = styled.div`
  ${props => props.theme.breakpoints.down("xs")} {
    h3 {
      padding: ${props => props.theme.spacing(0, 1)};
    }
  }
`;

export const CompanyEditPage: React.FunctionComponent<Props> = props => {
  const { isCreate } = props;
  const { t } = useResource();
  const dispatch = useDispatch();
  const history = useHistory();
  const { params } = useRouteMatch<RouteProps>();
  const companyID = params.id as any;
  const [company, setCompany] = React.useState<CompanyDto | null>(null);
  const [created, setCreated] = React.useState(false);

  const { companyDetail, isLoadingCompanyDetail: isLoading } = useSelector(
    (e: RootStateType) => e.companies,
  );

  React.useEffect(() => {
    dispatch(resetCompanyDetail());
    if (!isCreate) {
      dispatch(getCompaniesDetailAsync.request(companyID));
    }
    return () => {
      dispatch(resetCompanyDetail());
    };
  }, [dispatch, companyID, isCreate]);

  React.useEffect(() => {
    setCompany(isCreate ? newCompany : companyDetail);

    if (created && isCreate && companyDetail?.companyID) {
      dispatch(setDisableRedirect(false));
      history.replace(
        `/${t(Resources.Routes.Companies)}/${t(
          Resources.Routes.CompanyDetail,
        )}/${companyDetail.companyID}`,
      );
      setCreated(false);
    }
  }, [dispatch, companyDetail, history, isCreate, t, created]);

  const handleOnSave = (companyToSave: CompanyDto) => {
    setCreated(true);
    dispatch(
      saveCompanyAsync.request({
        company: {
          ...company,
          ...companyToSave,
        },
      }),
    );
    dispatch(setDisableRedirect(false));
  };

  const getBreadcrumbItems = () => {
    return [
      {
        name: t(Resources.Companies.Title),
        url: t(Resources.Routes.Companies),
      } as BreadcrumbItem,
      {
        name: isCreate
          ? t(Resources.CompanyEdit.NewCompany)
          : t(Resources.CompanyEdit.Breadcrumb.Title),
      } as BreadcrumbItem,
    ];
  };

  return (
    <StyledLayout>
      <AuthenticatedLayout
        title={
          isCreate
            ? t(Resources.CompanyEdit.NewCompany)
            : `${companyDetail?.name ?? ""}`
        }
        hasSideMenu={!isNoU(companyID)}
        breadcrumbItems={getBreadcrumbItems()}
      >
        <CompaniesSideMenu companyID={companyID} />
        <div className={"container-small"}>
          <div className={"box"}>
            <div className={"box-header"}>
              {!isLoading && company ? (
                isCreate ? (
                  t(Resources.CompanyEdit.NewCompany)
                ) : (
                  t(Resources.CompanyEdit.Title)
                )
              ) : (
                <PrimarySkeleton height={30} />
              )}
            </div>
            <div className={"box-content"}>
              {!isLoading && company ? (
                <CompanyEditForm
                  company={company}
                  isLoading={isLoading}
                  onSave={handleOnSave}
                  created={created}
                />
              ) : (
                <>
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                  <PrimarySkeleton height={60} />
                </>
              )}
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    </StyledLayout>
  );
};
